import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const offerings = [
  { title: 'Dual Language (6th Grade)', body: 'Our Spanish-English Dual Language program continues into middle school, with 6th grade instruction spanning both languages.' },
  { title: 'ELA & Social Studies', body: 'Rich reading, writing, and discussion-based classes that develop critical thinkers and effective communicators.' },
  { title: 'Mathematics', body: 'A rigorous math sequence progressing from 6th grade math through pre-algebra and algebra coursework.' },
  { title: 'Science', body: 'Hands-on science investigation across life, earth, and physical science, aligned to NGSS standards.' },
  { title: 'Arts & Electives', body: 'Visual arts, music, and physical education remain a core part of the PS 96 middle school experience.' },
  { title: 'High School Readiness', body: 'Our school counselor supports students with high school research, applications, and the transition to 9th grade.' },
];

const MiddleSchoolPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />

    <main id="main-content" className="pt-0">
      {/* Hero */}
      <section
        className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
      >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <span className="text-sm font-semibold text-white tracking-tight">Academics</span>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Middle School
          </h1>
          <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
            Grades 6–8. Rigorous academics, student leadership, and preparation for high school success.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <a href="https://www.schools.nyc.gov/enrollment/enroll-grade-by-grade/middle-school" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-[#fdbb2e] text-[#00213a] font-black rounded-lg hover:bg-[#ffd04e] transition-colors duration-200 shadow-sm">
              Apply on MySchools <ArrowRightIcon />
            </a>
            <a href="/high-school-hub" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold rounded-lg border border-white/20 hover:bg-white/20 transition-colors duration-200">
              High School Hub <ArrowRightIcon />
            </a>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="max-w-4xl mx-auto px-6 lg:px-8 mt-12 mb-16">
        <div className="bg-white border border-gray-200 rounded-xl shadow-md p-10">
          <h2 className="text-3xl font-semibold text-gray-900 mb-4 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Grades 6 – 8
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg mb-4">
            Middle school is a pivotal time — and at PS 96, we make sure every student navigates it with strong academic support, caring teachers, and a community that believes in them.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Our middle school grades are small enough to feel like a family, yet offer the breadth of programming students need to explore their interests and prepare for the next chapter. Every 8th grader leaves ready for the rigors of high school.
          </p>
        </div>
      </section>

      {/* Offerings */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 mb-4">
              <span className="text-sm font-semibold text-gray-700">Curriculum</span>
            </div>
            <h2 className="text-4xl font-semibold text-gray-900 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              What Students Learn
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {offerings.map((o) => (
              <div key={o.title} className="bg-white border border-gray-200 rounded-xl p-7 hover:shadow-md hover:border-[#fdbb2e]/40 transition-all duration-200">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-[#00213a] mt-0.5 flex-shrink-0"><CheckIcon /></span>
                  <h3 className="font-semibold text-gray-900">{o.title}</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed pl-8">{o.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* High school support CTA */}
      <section className="bg-[#00213a] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-semibold text-white mb-4 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            8th Grade — What's Next?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Our school counselor begins high school planning in 7th grade. Students and families get one-on-one support through the entire application process.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="/high-school-hub" className="inline-flex items-center gap-2 px-8 py-4 bg-[#fdbb2e] text-[#00213a] font-black rounded-xl hover:bg-[#ffd04e] transition-colors duration-200 shadow-lg">
              High School Hub <ArrowRightIcon />
            </a>
            <a href="/contact-us" className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-xl border border-white/30 hover:bg-white/20 transition-colors duration-200">
              Contact Counselor
            </a>
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default MiddleSchoolPage;
